import { motion } from 'framer-motion';

export default function Features() {
  return (
    <section id="funktionsweise" className="py-24 px-6 bg-black relative overflow-hidden">
      {/* Background Glow */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#0066FF]/10 rounded-full blur-[100px]"></div>

      <div className="max-w-7xl mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-20"
        >
          <div className="inline-block px-4 py-2 bg-[#0066FF]/10 border border-[#0066FF]/20 rounded-full mb-6">
            <span className="text-sm font-semibold text-[#0066FF]">Zwei Wege zum Erfolg</span>
          </div>
          <h2 className="text-4xl md:text-6xl font-bold text-white mb-6">
            So funktioniert deine Leadgenerierung
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto leading-relaxed">
            Wähle deinen Weg: Personalisierte Landingpages für B2C oder intelligente Branchen-Suche für B2B
          </p>
        </motion.div>

        {/* Two Main Paths */}
        <div className="grid lg:grid-cols-2 gap-8 mb-20 max-w-5xl mx-auto">
          {/* Path 1: Landingpage (B2C) */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-gradient-to-br from-gray-900 to-black rounded-3xl p-8 border border-gray-800"
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 bg-green-500 rounded-2xl flex items-center justify-center">
                <i className="ri-pages-line text-white text-2xl"></i>
              </div>
              <div>
                <div className="text-xs text-green-400 font-semibold mb-1">WEG 1: B2C</div>
                <h3 className="text-xl font-bold text-white">Personalisierte Landingpages</h3>
              </div>
            </div>
            
            <p className="text-gray-400 mb-6">
              Erhalte deine eigene professionelle Landingpage mit individuellem Link. Perfekt für Social Media und Ad-Netzwerke.
            </p>

            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-green-500/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-green-400 font-bold text-sm">1</span>
                </div>
                <div>
                  <div className="text-white font-medium text-sm mb-1">Landingpage erhalten</div>
                  <div className="text-gray-400 text-xs">Dein persönlicher Link</div>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-green-500/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-green-400 font-bold text-sm">2</span>
                </div>
                <div>
                  <div className="text-white font-medium text-sm mb-1">Auf Social Media bewerben</div>
                  <div className="text-gray-400 text-xs">Facebook, Instagram, TikTok</div>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-green-500/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-green-400 font-bold text-sm">3</span>
                </div>
                <div>
                  <div className="text-white font-medium text-sm mb-1">Interessenten füllen Formular aus</div>
                  <div className="text-gray-400 text-xs">Automatische Vorqualifizierung</div>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-green-500/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-green-400 font-bold text-sm">4</span>
                </div>
                <div>
                  <div className="text-white font-medium text-sm mb-1">Lead in deinem CRM</div>
                  <div className="text-gray-400 text-xs">Sofortige Benachrichtigung</div>
                </div>
              </div>
            </div>

            <div className="mt-6 pt-6 border-t border-gray-800">
              <div className="flex items-center gap-2 text-green-400">
                <i className="ri-user-heart-line"></i>
                <span className="text-xs font-medium">Ideal für Endkunden & Privatpersonen</span>
              </div>
            </div>
          </motion.div>

          {/* Path 2: Branchen-Suche (B2B) */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="bg-gradient-to-br from-gray-900 to-black rounded-3xl p-8 border border-gray-800"
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 bg-[#0066FF] rounded-2xl flex items-center justify-center">
                <i className="ri-search-line text-white text-2xl"></i>
              </div>
              <div>
                <div className="text-xs text-[#0066FF] font-semibold mb-1">WEG 2: B2B</div>
                <h3 className="text-xl font-bold text-white">Intelligente Branchen-Suche</h3>
              </div>
            </div>
            
            <p className="text-gray-400 mb-6">
              Finde gezielt Unternehmen in deiner Region. Wähle zwischen Krypto-Neulingen oder bereits krypto-affinen Partnern.
            </p>

            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-[#0066FF]/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-[#0066FF] font-bold text-sm">1</span>
                </div>
                <div>
                  <div className="text-white font-medium text-sm mb-1">Stadt & Branche eingeben</div>
                  <div className="text-gray-400 text-xs">z.B. München + Finanzberater</div>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-[#0066FF]/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-[#0066FF] font-bold text-sm">2</span>
                </div>
                <div>
                  <div className="text-white font-medium text-sm mb-1">Krypto-Affinität wählen</div>
                  <div className="text-gray-400 text-xs">Neulinge oder bereits krypto-affin</div>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-[#0066FF]/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-[#0066FF] font-bold text-sm">3</span>
                </div>
                <div>
                  <div className="text-white font-medium text-sm mb-1">KI findet & analysiert Websites</div>
                  <div className="text-gray-400 text-xs">Automatische Bewertung & Scoring</div>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-[#0066FF]/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-[#0066FF] font-bold text-sm">4</span>
                </div>
                <div>
                  <div className="text-white font-medium text-sm mb-1">Kontaktdaten extrahieren</div>
                  <div className="text-gray-400 text-xs">E-Mail, Telefon, Social Media</div>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-[#0066FF]/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-[#0066FF] font-bold text-sm">5</span>
                </div>
                <div>
                  <div className="text-white font-medium text-sm mb-1">Direkt kontaktieren</div>
                  <div className="text-gray-400 text-xs">Alle Infos im CRM verfügbar</div>
                </div>
              </div>
            </div>

            <div className="mt-6 pt-6 border-t border-gray-800">
              <div className="flex items-center gap-2 text-[#0066FF]">
                <i className="ri-building-line"></i>
                <span className="text-xs font-medium">Finanzberater, Versicherungen, Agenturen</span>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Common Steps After Lead Generation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="bg-gradient-to-br from-gray-900 to-black rounded-3xl p-8 border border-gray-800"
        >
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold text-white mb-3">Nach der Lead-Generierung</h3>
            <p className="text-gray-400">Beide Wege führen zu deinem zentralen CRM-Dashboard</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <i className="ri-dashboard-line text-purple-400 text-2xl"></i>
              </div>
              <h4 className="text-white font-bold mb-2">Status-Pipeline verwalten</h4>
              <p className="text-gray-400 text-sm">Von "Neu" über "Qualifiziert" bis "Konvertiert"</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-yellow-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <i className="ri-file-list-line text-yellow-400 text-2xl"></i>
              </div>
              <h4 className="text-white font-bold mb-2">Notizen & Aktivitäten</h4>
              <p className="text-gray-400 text-sm">Dokumentiere alle Interaktionen & nächste Schritte</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-green-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <i className="ri-hand-heart-line text-green-400 text-2xl"></i>
              </div>
              <h4 className="text-white font-bold mb-2">Du entscheidest</h4>
              <p className="text-gray-400 text-sm">Volle Kontrolle über Timing & Kontaktaufnahme</p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
